'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Star,
  Check,
  ArrowRight,
  Shield,
  Smartphone,
  BarChart3,
  Lock,
  Headphones,
  Zap,
  BookOpen,
  Award,
  CreditCard,
  Clock,
  Search,
  Bitcoin,
  DollarSign,
  Landmark,
  Gem,
  GraduationCap,
  Video,
  Copy,
  Gauge,
  ShieldCheck,
  LineChart,
} from 'lucide-react';

/* ───────────────────────── DATA ───────────────────────── */

const tickers = [
  { symbol: 'TESLA', price: '178.32', change: '+1.24%' },
  { symbol: 'AMAZON', price: '186.49', change: '+0.87%' },
  { symbol: 'APPLE', price: '213.07', change: '-0.34%' },
  { symbol: 'USD/CHF', price: '0.8823', change: '+0.05%' },
  { symbol: 'NETFLIX', price: '628.40', change: '+2.11%' },
  { symbol: 'MICROSOFT', price: '422.86', change: '+0.63%' },
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'NVIDIA', price: '875.28', change: '+3.15%' },
  { symbol: 'Crude Oil', price: '78.34', change: '+0.67%' },
];

const heroInstruments = [
  { icon: '💱', name: 'EUR/USD', label: 'Forex' },
  { icon: '🥇', name: 'Gold', label: 'Commodity' },
  { icon: '🚗', name: 'Tesla', label: 'Stock' },
  { icon: '🛢️', name: 'CrudeOil', label: 'Commodity' },
];

const instrumentTabs = [
  { icon: Globe, label: 'All' },
  { icon: DollarSign, label: 'Forex' },
  { icon: Bitcoin, label: 'Crypto' },
  { icon: BarChart3, label: 'Stocks' },
  { icon: Landmark, label: 'Indices' },
  { icon: Gem, label: 'Commodities' },
  { icon: DollarSign, label: 'Options' },
];

const tradingConditions = [
  { icon: Gauge, label: 'Tight spreads' },
  { icon: LineChart, label: 'Up to 30:1 leverage' },
  { icon: TrendingUp, label: 'Low commissions' },
  { icon: Zap, label: 'Instant execution' },
];

const platforms = [
  { name: 'WebTrader', desc: 'A sleek, easy to use desktop trading platform', size: 'large' },
  { name: 'MetaTrader 4', desc: 'No. 1 trading platform', size: 'small' },
  { name: 'MetaTrader 5', desc: 'Advanced trading across +1,250 assets', size: 'small' },
  { name: 'DupliTrade', desc: 'Automated copy-trading platform', size: 'small' },
  { name: 'Mobile App', desc: 'Trade on the go with full features', size: 'small' },
];

const regulations = [
  { code: 'CY', name: 'CySEC', full: 'Cyprus Securities and Exchange Commission' },
  { code: 'AI', name: 'ASIC', full: 'Australian Securities & Investments Commission' },
  { code: 'FD', name: 'IIROC', full: 'Investment Industry Regulatory Organization of Canada' },
  { code: 'AD', name: 'ADGM', full: 'Abu Dhabi Global Markets Financial Services' },
  { code: 'CB', name: 'CBI', full: 'Central Bank of Ireland' },
  { code: 'FC', name: 'FSCA', full: "South Africa's Financial Sector Conduct Authority" },
  { code: 'JP', name: 'FFAJ', full: 'Financial Futures Association of Japan' },
  { code: 'IS', name: 'ISA', full: 'Israel Securities Authority' },
  { code: 'SF', name: 'SFC', full: 'Securities and Futures Commission' },
];

const awards = [
  { title: 'Best Online Trading Platform', source: 'Global Business Magazine', year: '2025' },
  {
    title: 'Best Mobile Trading Experience',
    source: 'International Business Magazine',
    year: '2025',
  },
  { title: 'Best Futures Trading Broker', source: 'World Business Stars', year: '2025' },
  { title: 'Best Customer Service Europe', source: 'World Economic Magazine', year: '2025' },
  { title: '#1 Risk Management Tool', source: 'ForexBrokers.com', year: '2025' },
  { title: 'Most Trusted Trading Platform', source: 'Finance Derivative', year: '2025' },
];

const educationCards = [
  {
    icon: GraduationCap,
    title: 'Trading Academy',
    desc: 'Learn with structured lessons. Easy steps to build trading skills. No experience needed.',
    link: 'Explore Courses',
  },
  {
    icon: BookOpen,
    title: 'Education Section',
    desc: 'Read articles and watch tutorials that cover every trading aspect. All topics, all levels.',
    link: 'Browse All Topics',
  },
  {
    icon: Video,
    title: 'Live Webinars',
    desc: 'Attend our weekly live webinars to analyse trends. Improve your trading skills with experts.',
    link: 'Reserve Your Spot',
  },
];

const differentiators = [
  {
    title: 'We Want You to Succeed',
    desc: 'From developing the best educational materials out there, to providing daily market analysis, updates and live webinars, we are as interested in your success as you are.',
  },
  {
    title: 'We Believe in Endless Possibilities',
    desc: "Access the world's most popular instruments, ranging from forex pairs to CFDs on stocks, indices, commodities, and cryptocurrencies — all at the palm of your hand.",
  },
  {
    title: 'Great Trading Conditions',
    desc: 'Hedging is allowed, without any restrictions on short selling and scalping. We provide fast and reliable order execution, as well as ultra-low spreads.',
  },
  {
    title: 'We Love to Stay in Touch!',
    desc: 'Stay up-to-date on the latest market news, promotions and offers! Follow us on social media and subscribe to our newsletter for the latest updates.',
  },
];

const trustLogos = ['GeoTrust', 'McAfee', 'BARCLAYS', 'Trustpilot', 'SECURE SSL'];

const paymentMethods = ['mastercard', 'VISA', 'PayPal', 'Skrill', 'NETELLER', 'Wire Transfer'];

const faqs = [
  {
    q: 'What is AtlasTrade?',
    a: 'AtlasTrade is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Join AtlasTrade', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier banks, completely separate from company operational funds. We are regulated across 9 jurisdictions worldwide.',
  },
  {
    q: 'What platforms do you offer?',
    a: 'We offer WebTrader, MetaTrader 4, MetaTrader 5, DupliTrade for automated trading, and fully-featured mobile apps for iOS and Android.',
  },
  {
    q: 'How can I withdraw my funds?',
    a: 'Withdrawals can be made via bank wire, credit/debit card, or e-wallets. Requests are processed within 1-2 business days with no fees on standard withdrawals.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'Forex Trading': [
    { label: 'What is Forex', href: '#' },
    { label: 'How to Trade', href: '#' },
    { label: 'Forex Pairs', href: '#' },
    { label: 'Forex Spreads', href: '#' },
  ],
  'CFD Trading': [
    { label: 'What Are CFDs', href: '#' },
    { label: 'Stock CFDs', href: '#' },
    { label: 'Commodity CFDs', href: '#' },
    { label: 'Index CFDs', href: '#' },
  ],
  Crypto: [
    { label: 'Bitcoin Trading', href: '#' },
    { label: 'Ethereum Trading', href: '#' },
    { label: 'Crypto CFDs', href: '#' },
  ],
  Platforms: [
    { label: 'WebTrader', href: '#' },
    { label: 'MetaTrader 4', href: '#' },
    { label: 'MetaTrader 5', href: '#' },
    { label: 'Mobile App', href: '#' },
    { label: 'DupliTrade', href: '#' },
  ],
  Education: [
    { label: 'Trading Academy', href: '#' },
    { label: 'Trading Videos', href: '#' },
    { label: 'Live Webinars', href: '#' },
    { label: 'Economic Calendar', href: '#' },
  ],
  About: [
    { label: 'Why AtlasTrade?', href: '#' },
    { label: 'Regulation', href: '#' },
    { label: 'Awards', href: '#' },
    { label: 'Contact Us', href: '#' },
    { label: 'Legal Documents', href: '#' },
  ],
};

/* ───────────────────────── PAGE ───────────────────────── */

export default function AtlasPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeInstrumentTab, setActiveInstrumentTab] = useState(0);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const navLinks = [
    { label: 'Products', href: '#products' },
    { label: 'Trading Platforms', href: '#platforms' },
    { label: 'Trading Info', href: '#info' },
    { label: 'Education', href: '#education' },
    { label: 'About', href: '#about' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── RISK DISCLAIMER ── */}
      <div className="bg-[#E63946] text-white text-[11px] py-2 px-4 text-center">
        Trading CFDs and FX Options entails risk and could result in the loss of your capital.
      </div>

      {/* ── HEADER ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#1B2033]/98 backdrop-blur-md shadow-lg' : 'bg-[#1B2033]'
        }`}
      >
        <div className="site-container flex items-center justify-between h-16">
          <Link href="/templates/atlas" className="flex items-center gap-1">
            <span className="text-2xl font-black italic tracking-tight text-white">
              ATLAS<span className="text-[#E63946]">TRADE</span>
            </span>
            <span className="text-[8px] text-[#8B92A5] uppercase tracking-widest ml-1 hidden sm:block">
              Trade with confidence
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[13px] font-medium text-[#B0B7C8] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link href="#" className="text-[13px] font-medium text-[#3B82F6] hover:text-[#60A5FA]">
              Login
            </Link>
            <Link
              href="#"
              className="bg-[#E63946] hover:bg-[#D32F3B] text-white text-[13px] font-bold px-5 py-2 rounded transition-colors"
            >
              Register Now
            </Link>
          </div>

          <button className="lg:hidden p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-[#1B2033] border-t border-white/5 overflow-hidden"
            >
              <div className="site-container py-4 flex flex-col gap-3">
                {navLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="text-sm text-[#B0B7C8] py-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  href="#"
                  className="bg-[#E63946] text-white text-sm font-bold px-5 py-2.5 rounded text-center mt-2"
                >
                  Register Now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO ── */}
      <section className="bg-gradient-to-b from-[#F0F1F5] to-[#E8EAF0] py-16 relative overflow-hidden">
        <div className="site-container relative z-10 grid md:grid-cols-2 gap-10 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-black italic text-[#1B2033] leading-tight mb-5">
              Award-Winning
              <br />
              Trading Experience
            </h1>
            <p className="text-[#5A6178] text-base leading-relaxed mb-6 max-w-md">
              Step into global markets and trade with confidence across Stocks, Crypto, FX,
              Commodities, Indices and more, with AtlasTrade, a highly regulated multi-asset broker.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Link
                href="#"
                className="bg-[#E63946] hover:bg-[#D32F3B] text-white font-bold px-7 py-3 rounded transition-colors"
              >
                Join AtlasTrade
              </Link>
              <a
                href="#"
                className="text-[#1B2033] font-semibold text-sm inline-flex items-center gap-1 hover:text-[#E63946] transition-colors"
              >
                Free Demo <ChevronRight className="w-4 h-4" />
              </a>
            </div>
            {/* Trustpilot + Awards */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-[#00B67A] text-[#00B67A]" />
                <span className="text-xs font-bold text-[#1B2033]">Trustpilot</span>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-5 h-5 bg-[#00B67A] flex items-center justify-center">
                      <Star className="w-3 h-3 fill-white text-white" />
                    </div>
                  ))}
                </div>
                <span className="text-[10px] text-[#5A6178]">
                  4.8 <span className="text-[#3B82F6] underline">12,094 reviews</span>
                </span>
              </div>
              {/* Laurel awards */}
              <div className="flex gap-3">
                {['Best\nTrading', 'Trusted\nBroker', 'Innovation\nAward'].map((a, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="text-[#1B2033]">
                      <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                        <path
                          d="M20 4l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z"
                          fill="#3B82F6"
                          opacity="0.15"
                        />
                        <path
                          d="M8 36c4-8 4-16 0-24M32 36c-4-8-4-16 0-24"
                          stroke="#3B82F6"
                          strokeWidth="1.5"
                          fill="none"
                          opacity="0.4"
                        />
                        <circle
                          cx="20"
                          cy="18"
                          r="7"
                          stroke="#3B82F6"
                          strokeWidth="1.5"
                          fill="none"
                          opacity="0.4"
                        />
                      </svg>
                    </div>
                    <p className="text-[8px] text-[#5A6178] text-center leading-tight whitespace-pre-line">
                      {a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right — Floating instrument cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative flex justify-center"
          >
            {/* Decorative circle */}
            <div className="w-[320px] h-[320px] rounded-full bg-gradient-to-br from-[#1B2033]/5 to-[#1B2033]/10 flex items-center justify-center relative">
              <svg viewBox="0 0 300 200" className="w-[260px] opacity-30">
                <path
                  d="M0,140 Q40,130 80,110 T160,80 T240,60 T300,40"
                  fill="none"
                  stroke="#1B2033"
                  strokeWidth="1.5"
                />
              </svg>
              {/* Floating cards */}
              {heroInstruments.map((inst, i) => {
                const positions = [
                  'absolute -top-2 right-0',
                  'absolute top-16 -right-8',
                  'absolute bottom-16 -right-4',
                  'absolute -bottom-2 right-8',
                ];
                return (
                  <motion.div
                    key={i}
                    animate={{ y: [0, i % 2 === 0 ? -6 : 6, 0] }}
                    transition={{ duration: 3 + i * 0.5, repeat: Infinity }}
                    className={`${positions[i]} bg-white rounded-lg shadow-lg shadow-black/8 px-4 py-3 flex items-center gap-3 border border-[#E5E7EB]`}
                  >
                    <span className="text-lg">{inst.icon}</span>
                    <div>
                      <p className="text-sm font-bold text-[#1B2033]">{inst.name}</p>
                      <p className="text-[10px] text-[#8B92A5]">{inst.label}</p>
                    </div>
                    <span className="text-[#E63946] text-xs font-semibold ml-2">Trade &gt;</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── INSTRUMENT SEARCH + CONDITIONS ── */}
      <section className="site-section bg-white" id="products">
        <div className="site-container grid md:grid-cols-2 gap-12 items-start">
          {/* Left — Search panel */}
          <div>
            <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
              {/* Search bar */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E7EB]">
                <Search className="w-5 h-5 text-[#8B92A5]" />
                <span className="text-sm text-[#8B92A5]">Search for an instrument</span>
              </div>
              {/* Category tabs */}
              <div className="flex gap-1 px-4 py-3 border-b border-[#E5E7EB] overflow-x-auto">
                {instrumentTabs.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveInstrumentTab(i)}
                    className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                      activeInstrumentTab === i
                        ? 'bg-[#1B2033] text-white'
                        : 'text-[#8B92A5] hover:bg-[#F5F6FA]'
                    }`}
                  >
                    <t.icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
              {/* "Most Popular" content */}
              <div className="p-5">
                <p className="text-sm font-semibold text-[#E63946] mb-4">Most Popular</p>
                <div className="space-y-3">
                  {[
                    { s: 'EUR/USD', p: '1.0847', c: '+0.12%', cat: 'Forex' },
                    { s: 'Gold', p: '2,987.50', c: '+0.45%', cat: 'Commodity' },
                    { s: 'BTC/USD', p: '87,245', c: '+2.34%', cat: 'Crypto' },
                    { s: 'Tesla', p: '178.32', c: '+1.24%', cat: 'Stock' },
                    { s: 'S&P 500', p: '5,842.3', c: '+0.32%', cat: 'Index' },
                  ].map((item) => (
                    <div
                      key={item.s}
                      className="flex items-center justify-between py-2 border-b border-[#F5F6FA] last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#F5F6FA] flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-[#5A6178]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#1B2033]">{item.s}</p>
                          <p className="text-[10px] text-[#8B92A5]">{item.cat}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[#1B2033]">{item.p}</p>
                        <p
                          className={`text-[10px] font-medium ${item.c.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}`}
                        >
                          {item.c}
                        </p>
                      </div>
                      <span className="text-[#E63946] text-xs font-semibold">Trade &gt;</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right — Trading Conditions */}
          <div className="md:pt-8">
            <h2 className="text-3xl md:text-4xl font-black italic text-[#1B2033] leading-tight mb-6">
              Transparent
              <br />
              Trading Conditions
              <br />
              for CFDs
            </h2>
            <div className="space-y-5 mb-8">
              {tradingConditions.map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <c.icon className="w-5 h-5 text-[#3B82F6]" />
                  <span className="text-[#1B2033] font-semibold">{c.label}</span>
                </div>
              ))}
            </div>
            <Link
              href="#"
              className="bg-[#E63946] hover:bg-[#D32F3B] text-white font-bold px-7 py-3 rounded transition-colors inline-block"
            >
              Explore CFDs
            </Link>
          </div>
        </div>
      </section>

      {/* ── MOBILE PLATFORMS BANNER ── */}
      <section className="bg-[#1B2033] py-16 relative overflow-hidden" id="platforms">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2033] via-[#252A40] to-[#1B2033]" />
        <div className="site-container relative z-10">
          <div className="text-center mb-4">
            <h2 className="text-3xl md:text-4xl font-black italic text-white">
              Powerful Mobile Platforms <span className="text-[#3B82F6]">You Can Trust</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10 items-center mt-12">
            {/* Left text */}
            <div>
              <p className="text-[#B0B7C8] text-base italic leading-relaxed mb-6">
                Enjoy a fully immersive trading experience across a range of powerful platforms
                tailored to all levels.
              </p>
              <div className="flex gap-3">
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-white" />
                  <div>
                    <p className="text-[7px] text-[#8B92A5]">GET IT ON</p>
                    <p className="text-[11px] font-semibold text-white">Google Play</p>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-white" />
                  <div>
                    <p className="text-[7px] text-[#8B92A5]">Download on the</p>
                    <p className="text-[11px] font-semibold text-white">App Store</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Center — Phone mockups */}
            <div className="flex justify-center relative">
              <div className="relative">
                {/* Tablet */}
                <div className="w-[200px] h-[280px] bg-[#0D1320] rounded-2xl border border-white/10 shadow-xl overflow-hidden">
                  <div className="bg-[#161D30] px-3 py-2 border-b border-white/5 flex items-center gap-1">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#EF4444]/50" />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]/50" />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]/50" />
                    </div>
                    <span className="text-[7px] text-[#8B92A5] ml-1">AtlasTrade</span>
                  </div>
                  <div className="p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white text-[9px] font-bold">EUR/USD</span>
                      <span className="text-[#10B981] text-[8px]">+0.12%</span>
                    </div>
                    <svg viewBox="0 0 170 80" className="w-full mb-2">
                      <defs>
                        <linearGradient id="atlasTab" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,60 Q20,55 40,45 T80,35 T120,28 T170,15"
                        fill="none"
                        stroke="#3B82F6"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M0,60 Q20,55 40,45 T80,35 T120,28 T170,15 L170,80 L0,80 Z"
                        fill="url(#atlasTab)"
                      />
                    </svg>
                    {/* Mini donut chart */}
                    <div className="flex gap-3 items-center">
                      <svg width="40" height="40" viewBox="0 0 40 40">
                        <circle
                          cx="20"
                          cy="20"
                          r="15"
                          fill="none"
                          stroke="#3B82F6"
                          strokeWidth="5"
                          strokeDasharray="60 94.2"
                          transform="rotate(-90 20 20)"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="15"
                          fill="none"
                          stroke="#10B981"
                          strokeWidth="5"
                          strokeDasharray="25 94.2"
                          strokeDashoffset="-60"
                          transform="rotate(-90 20 20)"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="15"
                          fill="none"
                          stroke="#F59E0B"
                          strokeWidth="5"
                          strokeDasharray="9.2 94.2"
                          strokeDashoffset="-85"
                          transform="rotate(-90 20 20)"
                        />
                      </svg>
                      <div className="text-[7px] text-[#8B92A5] space-y-0.5">
                        <p>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3B82F6] mr-1" />
                          Forex 64%
                        </p>
                        <p>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10B981] mr-1" />
                          Stocks 26%
                        </p>
                        <p>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F59E0B] mr-1" />
                          Crypto 10%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Phone overlapping */}
                <div className="absolute -right-14 top-4 w-[120px] h-[240px] bg-[#0D1320] rounded-[20px] border border-white/10 shadow-2xl overflow-hidden z-10">
                  <div className="bg-[#161D30] px-2 py-1.5 border-b border-white/5 text-center">
                    <span className="text-[7px] text-white font-bold">AtlasTrade</span>
                  </div>
                  <div className="p-2">
                    <p className="text-[8px] text-white font-bold">Markets Traded</p>
                    <p className="text-white text-lg font-black">1.09255</p>
                    <p className="text-[#10B981] text-[8px]">Open</p>
                    <svg viewBox="0 0 100 50" className="w-full my-1.5">
                      <path
                        d="M0,35 Q12,30 25,25 T50,18 T75,22 T100,10"
                        fill="none"
                        stroke="#10B981"
                        strokeWidth="1.5"
                      />
                    </svg>
                    <div className="flex gap-1 mt-1">
                      {['13.77', '13.33', '13.61'].map((v, i) => (
                        <div
                          key={i}
                          className="bg-white/5 rounded px-1 py-0.5 text-[6px] text-[#8B92A5] text-center flex-1"
                        >
                          {v}%
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Right — Platform links */}
            <div className="space-y-3">
              {[
                'AtlasTrade Mobile App',
                'Mobile WebTrader',
                'AtlasSocial',
                'AvaOptions',
                'MetaTrader 4/5',
              ].map((p, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-5 py-3 rounded-lg border transition-colors cursor-pointer ${
                    i === 2
                      ? 'border-[#E63946]/30 bg-[#E63946]/5'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <span
                    className={`text-sm font-semibold ${i === 2 ? 'text-[#E63946]' : 'text-white'}`}
                  >
                    {p}
                  </span>
                  {i === 2 && (
                    <p className="text-[10px] text-[#B0B7C8]">
                      Copy the world&apos;s best traders.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PLATFORM GRID ── */}
      <section className="site-section bg-white">
        <div className="site-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black italic text-[#1B2033]">
              Endless Trading Opportunities Await
            </h2>
            <p className="text-[#8B92A5] mt-2">
              Try our advanced platforms and never miss a trade!
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {platforms.map((p, i) => {
              const isLarge = p.size === 'large';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`relative rounded-xl overflow-hidden group cursor-pointer ${
                    isLarge ? 'md:col-span-2 md:row-span-2 h-[320px]' : 'h-[150px]'
                  }`}
                >
                  {/* Dark bg simulating screenshot */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0D1320] to-[#1B2540]">
                    {/* Simulated UI lines */}
                    <div className="absolute inset-0 p-4 opacity-20">
                      <div className="w-full h-px bg-white/20 mb-3" />
                      <div className="flex gap-2">
                        <div className="w-16 h-2 bg-white/10 rounded" />
                        <div className="w-12 h-2 bg-white/10 rounded" />
                        <div className="w-20 h-2 bg-white/10 rounded" />
                      </div>
                      <svg viewBox="0 0 300 100" className="w-full mt-4">
                        <path
                          d={`M0,${60 + i * 5} Q40,${50 + i * 3} 80,${40 - i * 2} T160,${35 - i * 3} T240,${25 + i * 2} T300,${15 - i}`}
                          fill="none"
                          stroke="#3B82F6"
                          strokeWidth="1"
                        />
                      </svg>
                    </div>
                  </div>
                  {/* Label overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                    <h3 className="text-white font-bold text-lg">{p.name}</h3>
                    <p className="text-white/70 text-xs">{p.desc}</p>
                    <a className="text-xs text-white/80 mt-2 inline-flex items-center gap-1 group-hover:text-white transition-colors">
                      Learn More <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── REGULATION — WORLD MAP ── */}
      <section className="site-section bg-white" id="about">
        <div className="site-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black italic text-[#1B2033]">
              You&apos;re in Safe Hands
            </h2>
            <p className="text-[#8B92A5] mt-2">
              AtlasTrade is regulated across 9 jurisdictions, with a wide global corporate presence.
            </p>
            <Link
              href="#"
              className="inline-block mt-4 bg-[#E63946] hover:bg-[#D32F3B] text-white font-bold px-6 py-2.5 rounded transition-colors"
            >
              Register Now
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left — Globe/dots visualization */}
            <div className="flex justify-center">
              <div className="relative w-[320px] h-[320px]">
                {/* Circle outline */}
                <div className="absolute inset-0 rounded-full border-2 border-[#E5E7EB]" />
                {/* Dots representing countries */}
                {[
                  { x: '30%', y: '25%' },
                  { x: '45%', y: '22%' },
                  { x: '55%', y: '20%' },
                  { x: '60%', y: '35%' },
                  { x: '35%', y: '45%' },
                  { x: '50%', y: '50%' },
                  { x: '70%', y: '40%' },
                  { x: '25%', y: '60%' },
                  { x: '40%', y: '65%' },
                  { x: '75%', y: '55%' },
                  { x: '65%', y: '60%' },
                  { x: '55%', y: '70%' },
                  { x: '42%', y: '35%' },
                  { x: '58%', y: '45%' },
                  { x: '48%', y: '42%' },
                ].map((dot, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-[#3B82F6]/40"
                    style={{ left: dot.x, top: dot.y }}
                  />
                ))}
                {/* Highlighted regulation dots */}
                {[
                  { x: '42%', y: '28%', label: 'EU' },
                  { x: '75%', y: '35%', label: 'JP' },
                  { x: '25%', y: '55%', label: 'ZA' },
                  { x: '80%', y: '60%', label: 'AU' },
                  { x: '55%', y: '32%', label: 'AE' },
                ].map((dot, i) => (
                  <motion.div
                    key={i}
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                    className="absolute w-3 h-3 rounded-full bg-[#F59E0B] border-2 border-white shadow-sm"
                    style={{ left: dot.x, top: dot.y }}
                    title={dot.label}
                  />
                ))}
                {/* Connection lines (simple SVG) */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 320">
                  <line
                    x1="134"
                    y1="90"
                    x2="176"
                    y2="102"
                    stroke="#3B82F6"
                    strokeWidth="0.5"
                    opacity="0.3"
                  />
                  <line
                    x1="176"
                    y1="102"
                    x2="240"
                    y2="112"
                    stroke="#3B82F6"
                    strokeWidth="0.5"
                    opacity="0.3"
                  />
                  <line
                    x1="134"
                    y1="90"
                    x2="80"
                    y2="176"
                    stroke="#3B82F6"
                    strokeWidth="0.5"
                    opacity="0.3"
                  />
                  <line
                    x1="240"
                    y1="112"
                    x2="256"
                    y2="192"
                    stroke="#3B82F6"
                    strokeWidth="0.5"
                    opacity="0.3"
                  />
                </svg>
              </div>
            </div>
            {/* Right — Regulator list */}
            <div className="space-y-3">
              {regulations.map((r, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{r.code}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#3B82F6]">{r.name}</p>
                    <p className="text-xs text-[#8B92A5]">{r.full}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AWARDS ── */}
      <section className="py-16 bg-[#F5F6FA] border-y border-[#E5E7EB]">
        <div className="site-container">
          <h2 className="text-3xl font-black italic text-[#1B2033] text-center mb-10">
            Our <span className="text-[#E63946]">Awards</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {awards.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="text-center"
              >
                <div className="flex justify-center mb-2">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path
                      d="M8 38c4-10 4-18 0-28M32 38c-4-10-4-18 0-28"
                      stroke="#3B82F6"
                      strokeWidth="1.5"
                      fill="none"
                      opacity="0.5"
                    />
                    <circle
                      cx="20"
                      cy="18"
                      r="8"
                      stroke="#3B82F6"
                      strokeWidth="1.5"
                      fill="#3B82F6"
                      fillOpacity="0.1"
                    />
                    <path d="M17 18l2 2 4-4" stroke="#3B82F6" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>
                <p className="text-xs font-bold text-[#1B2033] leading-tight">{a.title}</p>
                <p className="text-3xl font-black text-[#1B2033] my-1">{a.year}</p>
                <p className="text-[10px] text-[#8B92A5]">{a.source}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDUCATION ── */}
      <section className="site-section bg-white" id="education">
        <div className="site-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black italic text-[#1B2033]">
              Trading Starts Here
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {educationCards.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <c.icon className="w-10 h-10 text-[#1B2033] mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-bold text-[#1B2033] mb-3">{c.title}</h3>
                <p className="text-sm text-[#5A6178] leading-relaxed mb-4">{c.desc}</p>
                <a
                  href="#"
                  className="text-[#E63946] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {c.link} <ChevronRight className="w-4 h-4" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT MAKES DIFFERENT (DARK) ── */}
      <section className="bg-[#1B2033] py-20">
        <div className="site-container">
          <h2 className="text-3xl md:text-4xl font-black italic text-white mb-3">
            What Makes AtlasTrade Different?
          </h2>
          <p className="text-[#B0B7C8] mb-12">
            Join Over 1 Million Returning Clients who already made the right choice
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators.map((d, i) => (
              <div key={i}>
                <div className="h-0.5 w-12 bg-[#E63946] mb-4" />
                <h3 className="text-white font-bold italic text-lg mb-3">{d.title}</h3>
                <p className="text-[#B0B7C8] text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY + PAYMENT ── */}
      <section className="bg-white py-12 border-b border-[#E5E7EB]">
        <div className="site-container">
          <h3 className="text-center text-lg font-semibold text-[#1B2033] mb-6">Trusted By</h3>
          <div className="flex flex-wrap items-center justify-center gap-10 mb-12">
            {trustLogos.map((l) => (
              <span key={l} className="text-[#8B92A5] text-sm font-bold tracking-wider">
                {l}
              </span>
            ))}
          </div>
          <div className="border-t border-[#E5E7EB] pt-10">
            <h3 className="text-center text-lg font-semibold text-[#1B2033] mb-6">
              Payment Methods
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-8">
              {paymentMethods.map((p) => (
                <span key={p} className="text-[#5A6178] text-sm font-bold tracking-wider">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="site-section bg-[#F5F6FA]" id="info">
        <div className="site-container">
          <h2 className="text-2xl font-black italic text-[#1B2033] mb-8">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-[#1B2033] text-sm">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#E63946] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-[#5A6178] leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#1B2033] pt-14 pb-6">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-10">
            {Object.entries(footerLinks).map(([cat, links]) => (
              <div key={cat}>
                <h4 className="text-white font-semibold text-sm mb-4">{cat}</h4>
                <ul className="space-y-2">
                  {links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-[#8B92A5] text-xs hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-8 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <span className="text-xl font-black italic text-white">
                ATLAS<span className="text-[#E63946]">TRADE</span>
              </span>
              {/* Newsletter */}
              <div className="flex gap-2">
                <div className="flex items-center bg-white/5 border border-white/10 rounded px-3 py-2 min-w-[200px]">
                  <span className="text-xs text-[#8B92A5]">Enter your email</span>
                </div>
                <button className="bg-[#E63946] text-white px-4 py-2 rounded text-xs font-bold">
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <p className="text-xs text-[#8B92A5]">Follow Us</p>
            <div className="flex gap-3">
              {[
                {
                  color: '#FF0000',
                  path: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
                },
                {
                  color: '#E4405F',
                  path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
                },
                {
                  color: '#1DA1F2',
                  path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
                },
                {
                  color: '#0A66C2',
                  path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
                },
                {
                  color: '#0088CC',
                  path: 'M11.944 0A12 12 0 000 12a12 12 0 0012 24 12 12 0 0012-12A12.02 12.02 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
                },
              ].map((s, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                  style={{ backgroundColor: s.color }}
                >
                  <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                    <path d={s.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5 pt-6">
            <p className="text-[10px] text-[#5A6178] leading-relaxed mb-3">
              AtlasTrade EU Ltd is regulated by the Central Bank of Ireland. AtlasTrade Capital
              Markets Pty Ltd is regulated by the ASIC. AtlasTrade Middle East Ltd is regulated by
              the Abu Dhabi Global Markets (ADGM) Financial Regulatory Authority (FRSA). Read
              AtlasTrade risk disclosure before trading Forex, CFDs, Spread-betting or FX Options.
              CFDs are complex instruments and come with a high risk of losing money rapidly due to
              leverage. You should consider whether you understand how CFDs work and whether you can
              afford to take the high risk of losing your money.
            </p>
            <p className="text-[10px] text-[#3D4A5E]">
              &copy; 2007-2026 AtlasTrade Markets Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ── BOTTOM TICKER BAR ── */}
      <div className="bg-[#12172A] border-t border-white/5 py-2.5 overflow-hidden">
        <div className="ticker-animate flex gap-8 whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-xs">
              <span className="font-bold text-white/80">{t.symbol}</span>
              <span className="text-[#8B92A5]">{t.price}</span>
              <span className={t.change.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                ▲{t.change}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
